import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { PoComboFilter, PoComboOption } from '@po-ui/ng-components';

import { ReviewService } from '../../review.service';
import { JiraIssue } from '../../../core/interfaces/job.interface';
import { getApiErrorMessage } from '../../../core/utils/http-error-message.util';

@Injectable()
export class JiraIssueComboFilterService implements PoComboFilter {
  private readonly pageSize = 50;

  /**
   * Avisa o passo sobre a última busca: mensagem quando falha, string vazia quando dá
   * certo.
   *
   * O contrato do PoComboFilter só admite devolver opções, então uma falha virava lista
   * vazia — indistinguível, para quem olha, de "você não tem issue nenhuma". A falha
   * precisa chegar à tela por fora.
   */
  notify: ((error: string) => void) | null = null;

  /**
   * As issues, carregadas uma vez.
   *
   * O filtro é LOCAL — a lista tem cinquenta itens e o serviço já a filtra em memória —,
   * então buscar de novo a cada tecla era uma requisição por caractere digitado sobre os
   * mesmos dados. `shareReplay` guarda o resultado; `reset` o descarta.
   */
  private cached$: Observable<JiraIssue[]> | null = null;

  constructor(private service: ReviewService) {}

  /** Esquece o que foi carregado. Chamado ao sair do fluxo, junto do resto do estado. */
  reset(): void {
    this.cached$ = null;
  }

  private issues$(): Observable<JiraIssue[]> {
    if (!this.cached$) {
      this.cached$ = this.service.getMyJiraIssues(this.pageSize).pipe(
        map(res => res?.issues ?? []),
        tap(() => this.notify?.('')),
        catchError(err => {
          this.notify?.(getApiErrorMessage(err, 'Não foi possível listar as suas issues do Jira.'));
          this.cached$ = null;
          return of([] as JiraIssue[]);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.cached$;
  }

  // O termo digitado chega em `params.value`, conforme o contrato do PoComboFilter. Com
  // outro nome o filtro era sempre vazio e o combo listava as 50 issues, digitasse-se o
  // que digitasse — o po-combo não filtra por conta própria quando há serviço.
  getFilteredData(params: { page?: number; property?: string; value?: string }, _filterParams?: any): Observable<PoComboOption[]> {
    const filterValue = (params.value || '').trim().toLowerCase();

    return this.issues$().pipe(
      map(issues => {
        const sortedByRecency = [...issues].sort((a, b) => {
          const dateA = new Date(a.updated || a.created || 0).getTime();
          const dateB = new Date(b.updated || b.created || 0).getTime();
          return dateB - dateA;
        });

        const filteredIssues = !filterValue
          ? sortedByRecency
          : sortedByRecency.filter(i =>
              `${i.key} ${i.summary} ${i.status}`.toLowerCase().includes(filterValue)
            );

        return filteredIssues.map(i => {
          const updated = i.updated
            ? new Date(i.updated)
                .toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                .replace(',', '')
            : '';
          return {
            label: `${i.key} [${i.status}${updated ? ' · ' + updated : ''}] — ${i.summary}`,
            value: i.key
          };
        });
      })
    );
  }

  // Chamado pelo PO UI ao inicializar o combo com um valor já selecionado.
  // Busca o label completo da issue para exibição correta; fallback para a chave caso não encontre.
  getObjectByValue(value: string | number, _filterParams?: any): Observable<PoComboOption> {
    const key = String(value);
    return this.issues$().pipe(
      map(issues => {
        const issue = issues.find(i => i.key === key);
        if (!issue) { return { label: key, value }; }
        const updated = issue.updated
          ? new Date(issue.updated)
              .toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
              .replace(',', '')
          : '';
        return {
          label: `${issue.key} [${issue.status}${updated ? ' · ' + updated : ''}] — ${issue.summary}`,
          value
        };
      }),
      catchError(() => of({ label: key, value }))
    );
  }
}
